import React, { useContext, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    X, Trash2, Plus, Minus, CreditCard, 
    ArrowLeft, CheckCircle2, Smartphone, 
    ShieldCheck, Lock, Landmark
} from 'lucide-react';
import { AppContext } from '../context/AppContext';

const CartDrawer = () => {
    const { cart, setCart, isCartOpen, setIsCartOpen, user } = useContext(AppContext);
    const [checkoutStep, setCheckoutStep] = useState('cart'); // 'cart', 'summary', 'visa', 'mpesa'
    const [paymentPhone, setPaymentPhone] = useState(user?.phone || '');

    const updateQty = (id, delta) => {
        setCart(prev => prev.map(item => 
            item.id === id ? { ...item, qty: Math.max(1, item.qty + delta) } : item
        ));
    };

    const removeItem = (id) => setCart(prev => prev.filter(item => item.id !== id));
    const totalPrice = cart.reduce((sum, item) => sum + (item.price * item.qty), 0);

    const closeAndReset = () => {
        setIsCartOpen(false);
        setTimeout(() => setCheckoutStep('cart'), 500); // Reset step after drawer closes
    };

    // Helper to parse images safely
    const getFirstImage = (imgStr) => {
        try {
            let data = imgStr;
            while (typeof data === 'string') data = JSON.parse(data);
            const path = Array.isArray(data) ? data[0] : '';
            return path.startsWith('http') ? path : `${path}`;
        } catch (e) { return ''; }
    };

    return (
        <AnimatePresence>
            {isCartOpen && (
                <>
                    <motion.div 
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={closeAndReset}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70]"
                    />
                    
                    <motion.div 
                        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="fixed right-0 top-0 h-full w-full max-w-md bg-white dark:bg-[#0a0a0a] z-[80] shadow-[-20px_0_50px_rgba(0,0,0,0.2)] flex flex-col overflow-hidden"
                    >
                        {/* HEADER */}
                        <div className="p-8 pb-4 flex justify-between items-center border-b border-gray-100 dark:border-white/5">
                            <div className="flex items-center gap-3">
                                {checkoutStep !== 'cart' && (
                                    <button onClick={() => setCheckoutStep('cart')} className="p-2 hover:bg-gray-100 dark:hover:bg-white/5 rounded-full">
                                        <ArrowLeft size={20} />
                                    </button>
                                )}
                                <h2 className="text-2xl font-black tracking-tighter italic">
                                    SWIFT<span className="text-red-600">.</span>{checkoutStep === 'cart' ? 'CART' : 'PAY'}
                                </h2>
                            </div>
                            <button onClick={closeAndReset} className="p-2 hover:bg-red-500/10 rounded-full transition-colors"><X /></button>
                        </div>

                        {/* CONTENT AREA */}
                        <div className="flex-1 overflow-y-auto p-8 pt-4 scrollbar-hide">
                            <AnimatePresence mode="wait">
                                
                                {/* STEP 1: CART LIST */}
                                {checkoutStep === 'cart' && (
                                    <motion.div key="cart" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                                        {cart.length === 0 ? (
                                            <div className="py-20 text-center opacity-30 italic font-bold">Your hangar is empty.</div>
                                        ) : (
                                            cart.map((item) => (
                                                <div key={item.id} className="flex gap-4 p-4 rounded-[2rem] bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 items-center">
                                                    <img src={getFirstImage(item.images)} className="w-16 h-16 object-contain rounded-xl bg-white dark:bg-black/20 p-2" alt=""/>
                                                    <div className="flex-1 min-w-0">
                                                        <h4 className="font-bold truncate text-sm">{item.model_name}</h4>
                                                        <p className="text-red-600 font-black text-sm">Ksh {Number(item.price).toLocaleString()}</p>
                                                        <div className="flex items-center gap-4 mt-2">
                                                            <div className="flex items-center gap-3 bg-white dark:bg-black/20 rounded-full px-2 py-1 border border-gray-200 dark:border-white/10">
                                                                <button onClick={() => updateQty(item.id, -1)} className="hover:text-red-500"><Minus size={12}/></button>
                                                                <span className="text-xs font-black">{item.qty}</span>
                                                                <button onClick={() => updateQty(item.id, 1)} className="hover:text-green-500"><Plus size={12}/></button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button onClick={() => removeItem(item.id)} className="text-gray-400 hover:text-red-500 p-2"><Trash2 size={18}/></button>
                                                </div>
                                            ))
                                        )}
                                    </motion.div>
                                )}

                                {/* STEP 2: SUMMARY & METHOD SELECTION */}
                                {checkoutStep === 'summary' && (
                                    <motion.div key="summary" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-8">
                                        <div className="p-6 bg-red-600/5 rounded-[2.5rem] border border-red-600/10">
                                            <h3 className="text-lg font-black mb-1">Jambo, <span className="text-red-600">{user?.name?.split(' ')[0]}!</span></h3>
                                            <p className="text-xs text-gray-500 font-medium leading-relaxed">You are about to authorize the deployment of <span className="font-bold text-slate-800 dark:text-white">{cart.length} items</span> to your location.</p>
                                        </div>

                                        <div className="space-y-3">
                                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 px-2">Select Payment Gateway</label>
                                            
                                            <button 
                                                onClick={() => setCheckoutStep('visa')}
                                                className="w-full p-6 rounded-[2rem] border border-gray-200 dark:border-white/10 flex items-center justify-between hover:border-red-600 transition-all bg-white dark:bg-transparent group"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center text-white shadow-lg">
                                                        <CreditCard size={24} />
                                                    </div>
                                                    <div className="text-left">
                                                        <p className="font-black text-sm uppercase italic">Visa / Mastercard</p>
                                                        <p className="text-[10px] opacity-50">Global Secure Checkout</p>
                                                    </div>
                                                </div>
                                                <CheckCircle2 className="text-gray-200 group-hover:text-red-600" size={20} />
                                            </button>

                                            <button 
                                                onClick={() => setCheckoutStep('mpesa')}
                                                className="w-full p-6 rounded-[2rem] border border-gray-200 dark:border-white/10 flex items-center justify-between hover:border-green-600 transition-all bg-white dark:bg-transparent group"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center text-white shadow-lg font-black text-xs">
                                                        MPESA
                                                    </div>
                                                    <div className="text-left">
                                                        <p className="font-black text-sm uppercase italic">Safaricom M-Pesa</p>
                                                        <p className="text-[10px] opacity-50">Instant STK Push</p>
                                                    </div>
                                                </div>
                                                <Smartphone className="text-gray-200 group-hover:text-green-600" size={20} />
                                            </button>
                                        </div>
                                    </motion.div>
                                )}

                                {/* STEP 3: VISA DETAILS */}
                                {checkoutStep === 'visa' && (
                                    <motion.div key="visa" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-6">
                                        <div className="bg-gradient-to-br from-slate-800 to-black p-6 rounded-[2rem] text-white shadow-2xl relative overflow-hidden">
                                            <div className="absolute top-0 right-0 p-4 opacity-20"><Landmark size={40}/></div>
                                            <p className="text-[10px] font-bold tracking-[4px] opacity-50 mb-8 uppercase">Swift Corporate Card</p>
                                            <div className="space-y-4">
                                                <input placeholder="0000 0000 0000 0000" className="w-full bg-transparent border-b border-white/20 py-2 outline-none text-xl font-mono tracking-widest placeholder:opacity-20" />
                                                <div className="flex gap-4">
                                                    <input placeholder="MM/YY" className="w-1/2 bg-transparent border-b border-white/20 py-2 outline-none text-sm font-mono" />
                                                    <input placeholder="CVC" className="w-1/2 bg-transparent border-b border-white/20 py-2 outline-none text-sm font-mono" />
                                                </div>
                                            </div>
                                        </div>
                                        <button className="w-full py-5 bg-black dark:bg-white dark:text-black text-white rounded-3xl font-black uppercase tracking-widest text-sm shadow-xl flex items-center justify-center gap-2">
                                            <Lock size={16} /> Pay Ksh {totalPrice.toLocaleString()}
                                        </button>
                                    </motion.div>
                                )}

                                {/* STEP 3: MPESA DETAILS */}
                                {checkoutStep === 'mpesa' && (
                                    <motion.div key="mpesa" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-6 text-center">
                                        <div className="w-20 h-20 bg-green-100 dark:bg-green-500/10 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <Smartphone size={40} />
                                        </div>
                                        <h3 className="text-xl font-black">STK Push Authorization</h3>
                                        <p className="text-xs text-gray-500 px-10">Enter your M-Pesa number below. You will receive a prompt on your phone to enter your PIN.</p>
                                        <div className="relative">
                                            <span className="absolute left-6 top-1/2 -translate-y-1/2 font-bold text-gray-400">+254</span>
                                            <input 
                                                type="tel" 
                                                value={paymentPhone}
                                                onChange={(e) => setPaymentPhone(e.target.value)}
                                                placeholder="700 000 000" 
                                                className="w-full p-5 pl-20 rounded-[2rem] bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 outline-none focus:border-green-500 font-bold text-lg" 
                                            />
                                        </div>
                                        <button className="w-full py-5 bg-green-600 text-white rounded-3xl font-black uppercase tracking-widest text-sm shadow-xl shadow-green-500/20">
                                            Initiate STK Push
                                        </button>
                                    </motion.div>
                                )}

                            </AnimatePresence>
                        </div>

                        {/* FOOTER SUMMARY */}
                        {cart.length > 0 && (
                            <div className="p-8 bg-gray-50 dark:bg-white/[0.02] border-t border-gray-100 dark:border-white/5">
                                <div className="flex justify-between items-end mb-6">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Total Settlement</span>
                                    <span className="text-3xl font-black text-slate-800 dark:text-white tracking-tighter">Ksh {totalPrice.toLocaleString()}</span>
                                </div>
                                {checkoutStep === 'cart' ? (
                                    <div className="flex gap-3">
                                        <button onClick={() => setCart([])} className="p-4 rounded-2xl bg-gray-100 dark:bg-white/5 text-gray-400 hover:text-red-500 transition-colors"><Trash2 size={20}/></button>
                                        <button 
                                            onClick={() => setCheckoutStep('summary')}
                                            className="flex-1 py-5 bg-red-600 hover:bg-red-700 text-white rounded-3xl font-black uppercase tracking-[2px] text-xs shadow-xl shadow-red-600/20 flex items-center justify-center gap-2 active:scale-95 transition-all"
                                        >
                                            Secure Checkout <CreditCard size={16}/>
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center gap-2 opacity-40 text-[10px] font-bold uppercase tracking-widest">
                                        <ShieldCheck size={14} /> 256-bit Encrypted Transaction
                                    </div>
                                )}
                            </div>
                        )}
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default CartDrawer;